import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import { ArrowRight, Shield, Zap, Bell, Hammer, Package } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';

export const Home = () => {
  const { t } = useTranslation();
  const { products, fetchProducts } = useApp();
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    fetchProducts({ featured: true });
  }, [fetchProducts]);

  useEffect(() => {
    setFeaturedProducts(products.filter(p => p.featured).slice(0, 4));
  }, [products]);

  const categories = [
    { icon: Zap, name: 'Stun Guns', key: 'taser', color: '#FF0099' },
    { icon: Bell, name: 'Alarms', key: 'alarm', color: '#FAFF00' },
    { icon: Shield, name: 'Kubatons', key: 'kubaton', color: '#00FFFF' },
    { icon: Package, name: 'Accessories', key: 'accessories', color: '#FF0099' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.3)' }}
        >
          <source src="https://customer-assets.emergentagent.com/job_fierce-defense/artifacts/rewigo0h_video%202.MOV" type="video/quicktime" />
          <source src="https://customer-assets.emergentagent.com/job_fierce-defense/artifacts/rewigo0h_video%202.MOV" type="video/mp4" />
        </video>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/50 to-[#050505]" />

        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-8xl font-black text-white mb-6 leading-none">
            <span className="block">FIGHT LIKE A</span>
            <span className="text-[#FF0099] neon-pink animate-pulse-glow inline-block">GYAL</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-[#A3A3A3] mb-8 max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop" data-testid="hero-cta">
              <Button className="btn-primary text-lg px-8 py-6 animate-pulse-glow">
                {t('hero.cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-3 bg-[#FF0099] rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="bg-[#FF0099] py-3 overflow-hidden">
        <Marquee gradient={false} speed={50}>
          <div className="flex items-center gap-8 px-4">
            {[...Array(4)].map((_, i) => (
              <React.Fragment key={i}>
                <span className="marquee-text text-black text-sm font-black">
                  {t('hero.marquee')}
                </span>
                <span className="text-black">★</span>
              </React.Fragment>
            ))}
          </div>
        </Marquee>
      </section>

      {/* Star Product Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#050505] to-[#0a0a0a] relative overflow-hidden" data-testid="star-product-section">
        {/* Glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FF0099]/20 rounded-full blur-[150px]" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <span className="font-accent text-[#FAFF00] text-lg">★ STAR PRODUCT ★</span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#FF0099] to-[#FAFF00] opacity-30 blur-xl group-hover:opacity-50 transition-opacity" />
              <img
                src="https://customer-assets.emergentagent.com/job_fierce-defense/artifacts/y0cqcfn4_Gemini_Generated_Image_5t23p35t23p35t23.png"
                alt="iShock Phone Stun Gun"
                className="relative w-full max-w-md mx-auto"
              />
            </div>
            
            {/* Content */}
            <div className="text-center lg:text-left">
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-black mb-4">
                iSHOCK <span className="text-[#FF0099] neon-pink">PHONE</span>
              </h2>
              <p className="text-[#A3A3A3] text-lg mb-6 max-w-xl">
                The ultimate disguise. Looks like a smartphone, delivers 2.8 million volts. 
                The element of surprise is your best defense.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center gap-6 mb-8">
                <div className="text-[#FF0099] font-heading font-black text-5xl">
                  €45.99
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#FAFF00]" />
                  <span className="text-[#FAFF00] font-bold">2.8M VOLTS</span>
                </div>
              </div>
              
              <Link to="/shop?category=taser" data-testid="star-product-cta">
                <Button className="btn-primary text-lg px-8 py-6 animate-pulse-glow">
                  Shop Stun Guns
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 px-4" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl text-white font-bold text-center mb-12">
            YOUR <span className="text-[#FF0099]">ARSENAL</span>
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {categories.map((cat) => (
              <Link
                key={cat.key}
                to={`/shop?category=${cat.key}`}
                className="group"
                data-testid={`category-${cat.key}`}
              >
                <div className="bg-[#1A1A1A] border border-[#333333] p-6 sm:p-8 text-center transition-all hover:border-[#FF0099] hover:shadow-[0_0_30px_rgba(255,0,153,0.2)]">
                  <cat.icon 
                    className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 transition-colors group-hover:text-[#FF0099]" 
                    style={{ color: cat.color }}
                  />
                  <h3 className="font-heading text-white font-bold uppercase tracking-wider text-sm sm:text-base">
                    {cat.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 px-4 bg-[#0a0a0a]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl text-white font-bold">
              <span className="text-[#FAFF00] font-accent text-lg sm:text-xl block mb-2" style={{ transform: 'rotate(-2deg)' }}>
                Best Sellers
              </span>
              FEATURED PRODUCTS
            </h2>
            <Link 
              to="/shop" 
              className="hidden sm:flex items-center gap-2 text-[#FF0099] font-bold uppercase tracking-wider hover:text-[#FAFF00] transition-colors"
              data-testid="view-all-link"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Link to="/shop" data-testid="view-all-mobile">
              <Button className="btn-secondary">
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Empowerment Section */}
      <section className="py-20 px-4" data-testid="empowerment-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-bold mb-6">
            YOUR RIGHT TO <span className="text-[#FF0099] neon-pink">DEFEND</span> YOURSELF
          </h2>
          <p className="text-[#A3A3A3] text-lg mb-8 leading-relaxed">
            In a world where women face violence every day, personal safety matters.
            We're here to help you feel confident, prepared, and supported in protecting yourself.
          </p>
          <p className="font-accent text-[#FAFF00] text-xl" style={{ transform: 'rotate(-1deg)' }}>
            Let fear change sides. ⚡
          </p>
        </div>
      </section>

      {/* Pack Promo */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#FF0099]/20 to-[#FAFF00]/20" data-testid="pack-promo-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="font-accent text-[#FAFF00] text-lg">Save €6!</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-bold mt-2 mb-6">
                ULTIMATE DEFENSE PACK
              </h2>
              <p className="text-[#A3A3A3] text-lg mb-6">
                Get everything you need in one bundle: Stun Gun + Alarm + Pepper Spray. 
                All in black. Complete protection at a special price.
              </p>
              <div className="flex items-center gap-4 mb-8">
                <span className="text-[#A3A3A3] line-through text-2xl">€65.99</span>
                <span className="text-[#FF0099] font-heading font-bold text-4xl">€59.99</span>
              </div>
              <Link to="/shop?category=pack" data-testid="pack-cta">
                <Button className="btn-primary">
                  Get the Pack
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_fierce-defense/artifacts/j4rb57id_Gemini_Generated_Image_pdxsn1pdxsn1pdxs.png"
                alt="Defense Pack"
                className="w-full rounded-lg shadow-[0_0_40px_rgba(255,0,153,0.3)]"
              />
              <div className="absolute -top-4 -right-4 bg-[#FAFF00] text-black font-accent text-sm px-4 py-2 transform rotate-12">
                BEST VALUE!
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
